import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { MapPin, Phone, Mail, Instagram, Facebook, Heart, Building2 } from 'lucide-react'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const location = useLocation()

  const footerLinks = {
    veranstaltungen: [
      { name: 'Veranstaltungen', href: '/veranstaltungen' },
      { name: 'Unsere Räume', href: '/raeume' },
      { name: 'Partys & Events', href: '/partys' },
      { name: 'Foodtruck', href: '/foodtruck' },
    ],
    unternehmen: [
      { name: 'Über uns', href: '/geschichte' },
      { name: 'Galerie', href: '/galerie' },
      { name: 'Jobs & Karriere', href: '/jobs' },
      { name: 'Kontakt', href: '/kontakt' },
    ],
    rechtliches: [
      { name: 'Impressum', href: '/impressum' },
      { name: 'Datenschutz', href: '/datenschutz' },
      { name: 'Cookie-Einstellungen', href: '/cookies' },
    ],
  }

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/hueckelsmay/' },
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/p/H%C3%BCckels-May-Events-61564139485480/' },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  }

  return (
    <footer className="relative bg-transparent text-white overflow-hidden border-t border-white/5">
      {/* Main Footer Content */}
      <div className="relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
          className="container mx-auto px-4 sm:px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-6 md:pb-8"
        >
          {/* Top Section - Logo & Tagline */}
          <motion.div variants={itemVariants} className="mb-16">
            <Link to="/" className="inline-block group">
              <img
                src="/Logo_HM.webp"
                alt="Hückels May"
                width={960}
                height={699}
                loading="lazy"
                decoding="async"
                className="h-12 md:h-16 w-auto mb-3 md:mb-4"
              />
            </Link>
            <p className="font-serif text-base md:text-lg text-gray-400 max-w-md italic">
              Historisches Gebäude von 1855 – Ihre Eventlocation seit 1998 im Herzen des Niederrheins
            </p>
            <div className="h-px w-20 bg-gradient-to-r from-brand-800 to-brand-900 mt-4"></div>
          </motion.div>

          {/* Links Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 lg:gap-10 mb-12 md:mb-16">
            {/* Veranstaltungen */}
            <motion.div variants={itemVariants}>
              <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-brand-400 mb-6 font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Veranstaltungen
              </h4>
              <ul className="space-y-3">
                {footerLinks.veranstaltungen.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-brand-400 transition-all duration-300 inline-flex items-center gap-2 group text-sm"
                    >
                      <motion.span
                        className="w-1 h-1 rounded-full bg-gray-600 group-hover:bg-[#8B1219] group-hover:w-1.5 transition-all"
                      />
                      <span className="font-light">{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Unternehmen */}
            <motion.div variants={itemVariants}>
              <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-brand-400 mb-6 font-semibold">
                Unternehmen
              </h4>
              <ul className="space-y-3">
                {footerLinks.unternehmen.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-brand-400 transition-all duration-300 inline-flex items-center gap-2 group text-sm"
                    >
                      <motion.span
                        className="w-1 h-1 rounded-full bg-gray-600 group-hover:bg-[#8B1219] group-hover:w-1.5 transition-all"
                      />
                      <span className="font-light">{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Rechtliches */}
            <motion.div variants={itemVariants}>
              <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-brand-400 mb-6 font-semibold">
                Rechtliches
              </h4>
              <ul className="space-y-3">
                {footerLinks.rechtliches.map((link) => {
                  // Spezielle Behandlung für Cookie-Einstellungen
                  if (link.href === '/cookies') {
                    return (
                      <li key={link.href}>
                        <Link
                          to={`${location.pathname}?showCookies=true`}
                          className="text-gray-400 hover:text-brand-400 transition-all duration-300 inline-flex items-center gap-2 group text-sm"
                        >
                          <motion.span
                            className="w-1 h-1 rounded-full bg-gray-600 group-hover:bg-[#8B1219] group-hover:w-1.5 transition-all"
                          />
                          <span className="font-light">{link.name}</span>
                        </Link>
                      </li>
                    )
                  }
                  return (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        className="text-gray-400 hover:text-brand-400 transition-all duration-300 inline-flex items-center gap-2 group text-sm"
                      >
                        <motion.span
                          className="w-1 h-1 rounded-full bg-gray-600 group-hover:bg-[#8B1219] group-hover:w-1.5 transition-all"
                        />
                        <span className="font-light">{link.name}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </motion.div>

            {/* Kontakt Info */}
            <motion.div variants={itemVariants}>
              <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-brand-400 mb-6 font-semibold">
                Kontakt
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3 group">
                  <MapPin className="w-4 h-4 text-brand-400 flex-shrink-0 mt-1" />
                  <address className="not-italic text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">
                    Hückelsmaystr. 70<br />
                    47804 Krefeld
                  </address>
                </div>
                <div className="flex items-center gap-3 group">
                  <Phone className="w-4 h-4 text-brand-400 flex-shrink-0" />
                  <a
                    href="tel:+4915755537615"
                    className="text-gray-400 text-sm hover:text-brand-400 transition-colors"
                  >
                    01575 5537615
                  </a>
                </div>
                <div className="flex items-center gap-3 group">
                  <Mail className="w-4 h-4 text-brand-400 flex-shrink-0" />
                  <a
                    href="mailto:info@hueckels-may.de"
                    className="text-gray-400 text-sm hover:text-brand-400 transition-colors break-all"
                  >
                    info@hueckels-may.de
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-8">
                <h5 className="text-xs uppercase tracking-wider text-gray-500 mb-4 font-semibold">
                  Folgen Sie uns
                </h5>
                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon
                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-xl bg-[#1a1a1a] border border-gray-800 hover:border-brand-800/50 flex items-center justify-center text-gray-400 hover:text-brand-400 active:scale-95 transition-all duration-200"
                        aria-label={social.name}
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    )
                  })}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          <motion.div
            variants={itemVariants}
            className="mb-8"
          >
            <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent"></div>
          </motion.div>

          {/* Bottom Bar */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4 text-center md:text-left"
          >
            <p className="text-gray-500 text-sm text-center md:text-left">
              &copy; {currentYear} Hückels May. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <p className="text-gray-600 flex items-center gap-2">
                Mit <Heart className="w-4 h-4 text-brand-800 fill-brand-800" /> erstellt von{' '}
                <a
                  href="https://herrjokic.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-400 hover:text-brand-300 transition-colors underline"
                >
                  Herr Jokic Agentur
                </a>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  )
}
